import { useCallback, useEffect, useMemo } from 'react';
import { useEditScreenStore } from '@/data/store/useEditScreenStore';
import { newFlowItemID, normalizeFlowItems } from '@/utils/serviceFlow';

/**
 * Service Flow template draft store (spec §12.5).
 *
 * Drafts are scoped by `orgID:accountID:templateID|'new'` in the shared
 * useEditScreenStore so they survive navigation to task creation (like other
 * edit screens) and never leak into global unscoped selection state.
 *
 * Hydration happens exactly once per fetched resource version — a background
 * refetch while the user has unsaved changes NEVER clobbers the draft; it only
 * raises `stale` so the UI can show "This template changed elsewhere".
 */

const STORE_SCREEN = 'ServiceFlowTemplateDraft';

const scopeKey = (organizationID, accountID, templateID) =>
    `${organizationID || 'no-org'}:${accountID || 'no-account'}:${templateID || 'new'}`;

const sameItems = (a = [], b = []) => {
    const strip = (items) => items.map(it => ({
        id: it.id,
        type: it.type,
        title: it.title,
        description: it.description,
        position: it.position,
        startOffsetMinutes: it.startOffsetMinutes,
        durationMinutes: it.durationMinutes,
        showTime: !!it.showTime,
        taskID: it.taskID,
        volunteerTeamID: it.volunteerTeamID,
    }));
    return JSON.stringify(strip(a)) === JSON.stringify(strip(b));
};

const draftIsDirty = (draft) => !!draft?.hydrated && (
    draft.name !== draft.baselineName
    || draft.description !== draft.baselineDescription
    || !sameItems(draft.items, draft.baselineItems)
);

export const useServiceFlowDraft = ({ organizationID, accountID, templateID = null, source = null, sourceLoading = false }) => {
    const key = scopeKey(organizationID, accountID, templateID);
    const draft = useEditScreenStore(s => s[STORE_SCREEN]?.[key]);
    const setEditScreenData = useEditScreenStore(s => s.setEditScreenData);
    const removeEditScreenData = useEditScreenStore(s => s.removeEditScreenData);

    const sourceVersion = source?.version ?? null;
    const dirty = useMemo(() => draftIsDirty(draft), [draft]);

    // Hydrate the draft from the fetched template (or initialize a new one).
    // Skips silently when the user already has local changes for this version's
    // draft — a newer source while dirty only flags `stale`.
    useEffect(() => {
        if (!organizationID || !accountID) return;
        if (templateID && sourceLoading) return;
        if (templateID && !source) return; // wait for the fetch (or error state)
        if (draft?.hydrated) {
            if (!draft.id) setEditScreenData(STORE_SCREEN, key, { id: templateID || newFlowItemID() });
            return;
        }

        const items = normalizeFlowItems(source?.items || []);
        setEditScreenData(STORE_SCREEN, key, {
            hydrated: true,
            id: templateID || newFlowItemID(),
            name: templateID ? (source?.name || '') : '',
            description: templateID ? (source?.description || '') : '',
            items,
            baselineItems: items,
            baselineName: templateID ? (source?.name || '') : '',
            baselineDescription: templateID ? (source?.description || '') : '',
            baselineVersion: sourceVersion ?? 0,
            stale: false,
        });
    }, [organizationID, accountID, templateID, source, sourceLoading, draft?.hydrated, draft?.id, sourceVersion, setEditScreenData, key]);

    // A refetched newer version while dirty → flag stale; while clean →
    // silently rehydrate so the draft tracks the latest saved template.
    useEffect(() => {
        if (!draft?.hydrated || sourceVersion === null) return;
        if (sourceVersion <= draft.baselineVersion) return;
        if (dirty) {
            if (!draft.stale) setEditScreenData(STORE_SCREEN, key, { stale: true });
            return;
        }
        const items = normalizeFlowItems(source?.items || []);
        setEditScreenData(STORE_SCREEN, key, {
            name: source?.name || '',
            description: source?.description || '',
            items,
            baselineItems: items,
            baselineName: source?.name || '',
            baselineDescription: source?.description || '',
            baselineVersion: sourceVersion,
            stale: false,
        });
    }, [sourceVersion, draft?.hydrated, dirty, draft?.stale, draft?.baselineVersion, source, setEditScreenData, key]);

    useEffect(() => () => {
        const current = useEditScreenStore.getState()[STORE_SCREEN]?.[key];
        if (!draftIsDirty(current)) removeEditScreenData(STORE_SCREEN, key);
    }, [key, removeEditScreenData]);

    const setMeta = useCallback((patch) => {
        setEditScreenData(STORE_SCREEN, key, patch);
    }, [setEditScreenData, key]);

    const setItems = useCallback((items) => {
        const normalized = items.map((it, i) => ({ ...it, position: i }));
        setEditScreenData(STORE_SCREEN, key, { items: normalized });
    }, [setEditScreenData, key]);

    const upsertItem = useCallback((item) => {
        const items = draft?.items || [];
        const index = items.findIndex(it => it.id === item.id);
        const next = [...items];
        if (index >= 0) next[index] = item;
        else next.push({ ...item, position: next.length });
        setItems(next);
    }, [draft?.items, setItems]);

    const removeItem = useCallback((itemID) => {
        setItems((draft?.items || []).filter(it => it.id !== itemID));
    }, [draft?.items, setItems]);

    const duplicateItem = useCallback((itemID, overrides = {}) => {
        const items = draft?.items || [];
        const index = items.findIndex(it => it.id === itemID);
        if (index < 0) return null;
        const sourceItem = items[index];
        const copy = {
            ...sourceItem,
            ...overrides,
            id: newFlowItemID(),
            templateItemID: null,
            taskInstanceID: null,
            TaskInstance: null,
            position: index + 1,
        };
        const next = [...items];
        next.splice(index + 1, 0, copy);
        setItems(next);
        return copy;
    }, [draft?.items, setItems]);

    const moveItem = useCallback((itemID, direction) => {
        const items = [...(draft?.items || [])].sort((a, b) => a.position - b.position);
        const index = items.findIndex(it => it.id === itemID);
        const target = index + direction;
        if (index < 0 || target < 0 || target >= items.length) return;
        const [moved] = items.splice(index, 1);
        items.splice(target, 0, moved);
        setItems(items);
    }, [draft?.items, setItems]);

    /** Initialize a new blank draft item of the given type (does not insert). */
    const createDraftItem = useCallback((type) => {
        const items = draft?.items || [];
        return {
            id: newFlowItemID(),
            type,
            title: '',
            description: null,
            position: items.length,
            startOffsetMinutes: null,
            durationMinutes: null,
            showTime: false,
            taskID: null,
            volunteerTeamID: null,
            templateItemID: null,
            taskInstanceID: null,
            Task: null,
            VolunteerTeam: null,
            TaskInstance: null,
        };
    }, [draft?.items]);

    const discard = useCallback(() => {
        const items = normalizeFlowItems(draft?.baselineItems || []);
        setEditScreenData(STORE_SCREEN, key, {
            name: draft?.baselineName || '',
            description: draft?.baselineDescription || '',
            items,
            stale: false,
        });
    }, [draft?.baselineItems, draft?.baselineName, draft?.baselineDescription, setEditScreenData, key]);

    /** After a successful save, adopt the server's version + items as baseline. */
    const markSaved = useCallback((result) => {
        const items = normalizeFlowItems(result?.items || draft?.items || []);
        setEditScreenData(STORE_SCREEN, key, {
            name: result?.name ?? draft?.name,
            description: result?.description ?? draft?.description,
            items,
            baselineItems: items,
            baselineName: result?.name ?? draft?.name,
            baselineDescription: result?.description ?? draft?.description,
            baselineVersion: result?.version ?? draft?.baselineVersion,
            stale: false,
        });
    }, [draft?.items, draft?.name, draft?.description, draft?.baselineVersion, setEditScreenData, key]);

    const clear = useCallback(() => {
        removeEditScreenData(STORE_SCREEN, key);
    }, [removeEditScreenData, key]);

    return {
        id: draft?.id,
        ready: !!draft?.hydrated,
        name: draft?.name ?? '',
        description: draft?.description ?? '',
        items: draft?.items || [],
        baselineVersion: draft?.baselineVersion ?? 0,
        dirty,
        stale: !!draft?.stale,
        setMeta,
        setItems,
        upsertItem,
        removeItem,
        duplicateItem,
        moveItem,
        createDraftItem,
        discard,
        markSaved,
        clear,
    };
};

/**
 * Read the live dirty flag outside render (e.g. beforeRemove handlers) so the
 * guard never captures a stale closure value — same pattern as forms.
 */
export const isServiceFlowDraftDirty = ({ organizationID, accountID, templateID = null }) => {
    const d = useEditScreenStore.getState()[STORE_SCREEN]?.[scopeKey(organizationID, accountID, templateID)];
    return draftIsDirty(d);
};
